import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import ArticleListItem from "@/components/feature/ArticleListItem";
import EmptyState from "@/components/feature/EmptyState";
import LoadingSpinner from "@/components/feature/LoadingSpinner";
import { categories } from "@/mocks/categories";
import { newsList } from "@/mocks/news";
import { getLatestArticles } from "@/utils/news";

type SearchSort = "relevance" | "latest";

const hotKeywords = ["林星野", "长风渡月", "片场", "版权", "演唱会", "综艺"];

function scoreArticle(title: string, summary: string, tags: string[], content: string[], q: string) {
  const keyword = q.toLowerCase();
  let score = 0;
  if (title.toLowerCase().includes(keyword)) score += 6;
  if (tags.some((tag) => tag.toLowerCase().includes(keyword))) score += 4;
  if (summary.toLowerCase().includes(keyword)) score += 2;
  if (content.some((paragraph) => paragraph.toLowerCase().includes(keyword))) score += 1;
  return score;
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [sort, setSort] = useState<SearchSort>("relevance");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(Boolean(query));

  usePageMeta({
    title: query ? `搜索「${query}」 - 黑料吃瓜网` : "站内搜索 - 黑料吃瓜网娱乐八卦资讯",
    description: query
      ? `黑料吃瓜网站内搜索「${query}」的结果，按相关度或时间排序，快速找到你关注的明星动态与热点事件。`
      : "搜索黑料吃瓜网的娱乐新闻、明星绯闻与社会热点，支持标题、标签与正文关键词匹配。",
    keywords: "黑料吃瓜网,搜索,娱乐八卦,明星新闻",
  });

  useEffect(() => {
    setInput(query);
    if (!query) {
      setSearched(false);
      return;
    }
    setSearched(true);
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 420);
    return () => window.clearTimeout(timer);
  }, [query]);

  const results = useMemo(() => {
    const keyword = query.trim();
    if (!keyword) return [];
    const matched = newsList.filter(
      (item) => scoreArticle(item.title, item.summary, item.tags, item.content, keyword) > 0,
    );
    if (sort === "latest") {
      return [...matched].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
    }
    return [...matched].sort(
      (a, b) =>
        scoreArticle(b.title, b.summary, b.tags, b.content, keyword) -
          scoreArticle(a.title, a.summary, a.tags, a.content, keyword) || b.views - a.views,
    );
  }, [query, sort]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const keyword = input.trim();
    if (!keyword) {
      setSearchParams({});
      return;
    }
    setSort("relevance");
    setSearchParams({ q: keyword });
  };

  const runKeyword = (keyword: string) => {
    setInput(keyword);
    setSort("relevance");
    setSearchParams({ q: keyword });
  };

  return (
    <>
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full max-w-[1100px] mx-auto px-4 md:px-6 py-7 md:py-10">
          <p className="text-[11px] tracking-[0.22em] text-primary-600 uppercase">Search</p>
          <h1 className="mt-2 font-heading text-2xl md:text-[32px] font-bold text-foreground-950">站内搜索</h1>
          <p className="mt-2 text-xs md:text-sm text-foreground-600">
            支持标题、摘要、标签与正文关键词匹配，演示数据共 {newsList.length} 篇。
          </p>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <div className="flex items-center gap-2 flex-1 bg-background-50 border border-background-300 rounded-md px-4 py-2.5 focus-within:border-primary-400 transition-colors">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入明星名字、剧集名称或关键词"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                aria-label="搜索关键词"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput("")}
                  className="w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:bg-background-200 cursor-pointer"
                  aria-label="清空输入"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索</span>
            {hotKeywords.map((keyword) => (
              <button
                key={keyword}
                type="button"
                onClick={() => runKeyword(keyword)}
                className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {keyword}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1100px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {searched ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-background-200">
              <p className="text-sm text-foreground-700">
                {loading ? (
                  "正在检索…"
                ) : (
                  <>
                    关键词 <strong className="text-primary-600 font-medium">「{query}」</strong> 共找到{" "}
                    <strong className="text-foreground-950">{results.length}</strong> 条结果
                  </>
                )}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex p-1 bg-background-200 rounded-full">
                  {[
                    { key: "relevance" as SearchSort, label: "相关度" },
                    { key: "latest" as SearchSort, label: "时间" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                        sort === option.key
                          ? "bg-background-50 text-primary-600 font-medium"
                          : "text-foreground-600"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
              <LoadingSpinner label="正在为你筛选相关内容…" className="py-16" />
            ) : results.length === 0 ? (
              <div className="mt-6">
                <EmptyState
                  icon="ri-search-eye-line"
                  title={`没有找到与「${query}」相关的内容`}
                  description="试试更换更短的关键词，例如只输入明星名字或剧名；也可以从下面的热门分类进入浏览。"
                  actionLabel="清空关键词重新搜索"
                  onAction={() => {
                    setInput("");
                    setSearchParams({});
                    setSearched(false);
                  }}
                />
                <div className="mt-6 bg-background-50 border border-background-200 rounded-lg p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">换个方向看看</h2>
                  <div className="flex flex-wrap gap-2.5">
                    {categories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/category/${category.id}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-100 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <i className={category.icon} />
                        {category.name}
                      </Link>
                    ))}
                    <Link
                      to="/topic/topic-drama-2026"
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary-50 text-xs text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-bookmark-3-line" />
                      热门专题
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 bg-background-50 border border-background-200 rounded-lg px-4 md:px-6">
                {results.map((article) => (
                  <ArticleListItem key={article.id} article={article} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8">
            <div className="bg-background-50 border border-dashed border-background-300 rounded-lg p-8 md:p-10 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
                <i className="ri-search-line text-2xl text-primary-500" />
              </div>
              <h2 className="font-heading text-lg font-bold text-foreground-950">输入关键词，开始找瓜</h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-md leading-relaxed">
                可以试试明星名字、剧集名称、栏目标签，或者直接输入「版权」「演唱会」这类话题词。
                搜索结果为前端演示数据，切换排序会实时刷新。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {hotKeywords.slice(0, 4).map((keyword) => (
                  <button
                    key={keyword}
                    type="button"
                    onClick={() => runKeyword(keyword)}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {keyword}
                  </button>
                ))}
              </div>
            </div>

            <aside>
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2 mb-3">
                <i className="ri-fire-line text-primary-500" />
                大家都在看
              </h2>
              <div className="bg-background-50 border border-background-200 rounded-lg divide-y divide-background-200">
                {getLatestArticles(4).map((article) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className="group flex gap-3 p-3 cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={article.cover}
                        alt={article.title}
                        title={`${article.title} - 黑料吃瓜网`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="text-xs leading-relaxed text-foreground-700 line-clamp-3 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </p>
                  </Link>
                ))}
              </div>
            </aside>
          </div>
        )}
      </div>
    </>
  );
}